import React, { useState, useEffect, useRef } from 'react';
import { AutoComplete, Input } from 'antd';
import { EnvironmentOutlined } from '@ant-design/icons';

/**
 * Location Autocomplete Component for Pakistan
 * Provides address suggestions using OpenStreetMap Nominatim API
 * 
 * @param {string} value - The current value of the input
 * @param {function} onChange - Callback function when value changes
 * @param {string} placeholder - Placeholder text for the input
 * @param {boolean} disabled - Whether the input is disabled
 */
const LocationAutocomplete = ({ value, onChange, placeholder, disabled }) => {
    const [options, setOptions] = useState([]);
    const [loading, setLoading] = useState(false);
    const searchTimeoutRef = useRef(null);

    // Fetch location suggestions from OpenStreetMap Nominatim API
    const fetchLocationSuggestions = async (query) => {
        if (!query || query.length < 2) {
            setOptions([]);
            return;
        }

        setLoading(true);
        try {
            // Use Nominatim API with Pakistan country code filter
            const response = await fetch(
                `https://nominatim.openstreetmap.org/search?` +
                `format=json&` +
                `q=${encodeURIComponent(query)}&` +
                `countrycodes=pk&` +
                `addressdetails=1&` +
                `limit=5&` +
                `accept-language=en`
            );

            if (response.ok) {
                const data = await response.json();
                const suggestions = data.map((item) => ({
                    value: item.display_name,
                    label: (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <EnvironmentOutlined style={{ color: '#1890ff' }} />
                            <div>
                                <div style={{ fontWeight: 500 }}>{item.display_name}</div>
                                {item.address && (
                                    <div style={{ fontSize: '12px', color: '#8c8c8c', marginTop: '2px' }}>
                                        {[
                                            item.address.city || item.address.town || item.address.village,
                                            item.address.state || item.address.province,
                                            'Pakistan'
                                        ].filter(Boolean).join(', ')}
                                    </div>
                                )}
                            </div>
                        </div>
                    ),
                    display_name: item.display_name,
                    lat: item.lat,
                    lon: item.lon
                }));
                setOptions(suggestions);
            } else {
                setOptions([]);
            }
        } catch (error) {
            console.error('Error fetching location suggestions:', error);
            setOptions([]);
        } finally {
            setLoading(false);
        }
    };

    // Debounce search to avoid too many API calls
    const handleSearch = (searchValue) => {
        if (onChange) {
            onChange(searchValue);
        }
        
        if (searchTimeoutRef.current) {
            clearTimeout(searchTimeoutRef.current);
        }

        searchTimeoutRef.current = setTimeout(() => {
            fetchLocationSuggestions(searchValue);
        }, 500); // Wait 500ms after user stops typing
    };

    const handleSelect = (selectedValue) => {
        if (onChange) {
            onChange(selectedValue);
        }
        setOptions([]);
    };

    // Cleanup timeout on unmount
    useEffect(() => {
        return () => {
            if (searchTimeoutRef.current) {
                clearTimeout(searchTimeoutRef.current);
            }
        };
    }, []);

    return (
        <AutoComplete
            options={options}
            onSearch={handleSearch}
            onSelect={handleSelect}
            value={value}
            disabled={disabled}
            loading={loading}
            notFoundContent={loading ? 'Searching...' : (value && value.length < 2) ? 'Type at least 2 characters' : null}
            style={{ width: '100%' }}
            filterOption={false} // We're handling filtering via API
            allowClear
        >
            <Input 
                prefix={<EnvironmentOutlined style={{ color: '#8c8c8c' }} />}
                placeholder={placeholder}
                disabled={disabled}
            />
        </AutoComplete>
    );
};

export default LocationAutocomplete;

